import { Injectable, Provider } from "@angular/core";
import { Observable, forkJoin, of, throwError } from "rxjs";
import { AuthService, AuthType, AUTH_TYPE_HEADER } from "./auth.service";
import { Addon } from "src/app/shared/models/addon.model";
import { map, switchMap, mergeMap, tap, shareReplay, catchError } from "rxjs/operators";
import { environment } from "src/environments/environment";
import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpRequest
} from "@angular/common/http";
import { ActivatedRouteSnapshot } from "@angular/router";
import { UserInfoService } from "../../requests/esp/common/services/userinfo-service";
import { EspDataProviderService } from "../../requests/esp/common/services/esp-data-provider.service";
import { CACHE_SKIP_HEADER } from "../inteceptors/http-cache.interceptor";
import { GroupsService } from '../../groups/groups.service';
import { OrganizationService } from '../../requests/esp/organization/services/organization.service';

@Injectable({
  providedIn: "root"
})
export class EspAuthService {
  constructor(
    private _authService: AuthService,
    private http: HttpClient,
    private _userInfoService: UserInfoService,
    private groupsService: GroupsService,
    private organizationService: OrganizationService
  ) { }

  getEspAuth(): Observable<Addon> {
    return this._authService
      .getAddonsAuth(this._authService.idenediGroupId)
      .pipe(
        map(addons => {
          return addons.find(
            addon => addon.Type.toLowerCase() == AuthType.ESP.toLowerCase()
          );
        })
      );
  }

  /**
   * Gets Simple Strata and Technadopt addons 
   */
  getRequiredAddons(): Observable<Addon[]> {
    return this._authService
      .getAddonsAuth(this._authService.idenediGroupId)
      .pipe(
        map(addons => {
          return addons.filter(
            addon => addon.Type.toLowerCase() == AuthType.SimpleStrata.toLowerCase() || addon.Type.toLowerCase() == AuthType.Technadopt.toLowerCase() 
          );
        })
      );
  }

  getIdenediCode(): Observable<string> {
    let url = `${environment.idenediProviderUrl}/api/oauth/AuthorizationCode?client_id=XSP1980031200`;
    if (this._authService.idenediGroupId) {
      url = `${environment.idenediProviderUrl}/api/oauth/AuthorizationCode?client_id=XSP1980031200&gIdenedi=${this._authService.idenediGroupId}`;
    }
    let headers = new HttpHeaders({
      [AUTH_TYPE_HEADER]: AuthType.Idenedi,
      [CACHE_SKIP_HEADER]: ''
    });
    const options = {
      headers: headers
    };

    return this.http.get<any>(url, options).pipe(
      map(code => code)
    );
  }

  setConnectedAppInfo(espAddon: Addon): Observable<Addon> {
      let customDomain = '';
      return this.groupsService.getGroupDetailsPublic(this._authService.idenediGroupId)
      .pipe(
        switchMap((groupInfo) => {
          if (groupInfo.GroupMembershipStatus == "Admin") {
            customDomain = groupInfo?.GroupSettings?.GroupApp?.CustomDomain
            return this.getRequiredAddons();
          } else {
            return of(null);
          }
        }),
        switchMap((addons: Addon[]) => {
          if (addons) {
            let ssAddon = addons.find(addon => addon.Type.toLowerCase() == AuthType.SimpleStrata.toLowerCase()); 
            let technadoptBaseUrl = '';
            if (addons.length > 0) {
              technadoptBaseUrl = addons.find(addon => addon.Type.toLowerCase() == AuthType.Technadopt.toLowerCase())?.BaseUrl;
            }
            return this.organizationService.setConnectedAppInfo(customDomain, ssAddon?.PublicProperties?.baseURL, ssAddon?.PublicProperties?.organziationId, technadoptBaseUrl, this.groupsService.groupFullName).pipe(
              map(() => espAddon)
            )

          } else {
            return of(espAddon);
          }
        })
      )
  }

  private espToken$: Observable<Addon> | null = null;
  getEspToken(): Observable<Addon> {
    // if (this.espToken$) {
    //   return this.espToken$;
    // }
    const isStemexeGroupAdmin = this.groupsService.isGroupAdmin();
    this.espToken$ = this.getEspAuth().pipe(
      switchMap(espAddon => {
        if (!espAddon) {
          return of(null);
        }
        if (espAddon.AccessToken) {
          return of(espAddon);
        }
        
        return this.getIdenediCode().pipe(
          switchMap(code => {
            const orgId = getEspOrgId(espAddon);

            const baseUrl = espAddon.BaseUrl.replace(/\/$/, "");
            return this.http.get<any>(
              `${baseUrl}/orguser/idenediTokenV3/${code}/${orgId}?gAdmin=${isStemexeGroupAdmin ? '1' : '0'}`
            ).pipe(
              map(resp => {
                espAddon.AccessToken = resp.token.accessToken;
                espAddon.OrganziationId = resp.user.organizationId;

                this._userInfoService.role = resp.user.role;
                this._userInfoService.isApplicant = resp.user.isApplicant;
                this._userInfoService.imageUrl = resp.user.imageUrl;
                this._userInfoService.organizationId = Number(resp.user.organizationId);

                return espAddon;
              })
            );
          }),
          switchMap(espAddon => this.setConnectedAppInfo(espAddon))
        );
      }),
      shareReplay(1),
      catchError(err => {
        // IMPORTANT: reset cache on error
        this.espToken$ = null;
        return throwError(() => err);
      })
    );

    return this.espToken$;
  }
}

//If organziationId is not present for main group read organziationId from sub groups
export function getEspOrgId(espAddon: Addon) {
  if (espAddon?.PublicProperties?.organziationId) {
    return espAddon?.PublicProperties?.organziationId;
  }

  let accessList = espAddon?.PublicProperties?.subgroupsAccessList ?? [];
  // Check if accessList is a string and attempt to parse it as JSON
  if (typeof accessList === 'string') {
    try {
      accessList = JSON.parse(accessList);
    } catch (e) {
      console.error('Failed to parse accessList:', e);
      accessList = []; // Set to an empty array if parsing fails
    }
  }

  const mappedAccessList = accessList.map(acl => Object.values(acl)[0]);
  if (mappedAccessList.length) {
    return mappedAccessList[0];
  }
  return null;
}

@Injectable({
  providedIn: "root"
})
export class EspTokenResolverService  {
  constructor(private _espAuthService: EspAuthService,
    private _espDataProviderService: EspDataProviderService,
    private _authService: AuthService) {
    this._espDataProviderService.initialize()
  }

  resolve(route: ActivatedRouteSnapshot = null): Observable<any> {
    if (!this._authService.isLoggedIn() && (route?.data?.ignoreToken == null || route?.data?.ignoreToken == true)) {
      return of(true)
    }
    // if (route?.data?.ignoreToken && !this._authService.isLoggedIn()) {
    //   return of(true)
    // }
    else {
      return this._espAuthService.getEspToken().pipe(map(espAddon => espAddon));
    }
  }
}
